'use client';

import type { Update } from '@baseball-near-you/prisma';
import { Button, cn } from '@baseball-near-you/ui';
import { dayjsExt } from '@bb/lib/dayjs';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useTeamBulletinMutation } from '@bb/lib/mutations/useTeamBulletinMutation';
import { ArrowLeft, ArrowRight, Clock, Trash2 } from 'lucide-react';
import { useState } from 'react';

export function BulletinBoard(props: {
  bulletins: Update[];
  className?: string;
}) {
  const { userProfile } = useUserProfile();
  const [currentItem, setCurrentItem] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const currentBulletin = props.bulletins[currentItem];

  const { deleteBulletin, deletingBulletin } = useTeamBulletinMutation({
    teamId: currentBulletin.teamId,
  });
  return (
    <div
      className={cn(
        'h-full w-full border-t border-t-gray-100 p-5',
        props.className
      )}
    >
      <div className="mb-2 flex w-full items-center justify-between">
        <p className="font-bold text-xl capitalize">Posts</p>
        <div className="flex space-x-4">
          <button
            className={cn(
              'rounded-sm border px-3 py-2 transition-all hover:scale-105 active:scale-95 md:p-1',
              currentItem === 0 &&
                'pointer-events-none border-gray-300 bg-gray-100 opacity-0'
            )}
            onClick={() => setCurrentItem((currentItem) => currentItem - 1)}
            type="button"
          >
            <ArrowLeft size={15} />
          </button>
          <button
            className={cn(
              'rounded-sm border px-3 py-2 transition-all hover:scale-105 active:scale-95 md:p-1',
              currentItem === props.bulletins.length - 1 &&
                'pointer-events-none border-gray-300 bg-gray-100 opacity-0'
            )}
            onClick={() => setCurrentItem((currentItem) => currentItem + 1)}
            type="button"
          >
            <ArrowRight size={15} />
          </button>
          <div className="relative">
            {confirmDelete && (
              <div className="absolute right-0 bottom-full z-10 mb-2 flex flex-col rounded-md border bg-white p-2 shadow-md">
                <p className="mb-2 w-40 font-medium text-sm">
                  Are you sure you want to delete this post?
                </p>
                <div className="flex space-x-2 text-sm">
                  <Button
                    variant="outline"
                    className="!py-1 h-min text-sm"
                    disabled={deletingBulletin}
                    onClick={() => {
                      setConfirmDelete(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="!py-1 h-min text-sm"
                    loading={deletingBulletin}
                    onClick={async () => {
                      await deleteBulletin(currentBulletin.id);
                      setConfirmDelete(false);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
            {!!userProfile?.UserTeam.find(
              ({ team }) => team.id === currentBulletin.teamId
            ) && (
              <button
                type="button"
                className="flex h-full w-8 items-center justify-center rounded-sm border border-red/50 p-1 text-red md:w-6"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
      <div>{currentBulletin?.body}</div>
      <div className="mt-4 flex justify-between text-gray-500 text-xs">
        <div className="flex items-center">
          <Clock size={12} />
          <p className="ml-1.5">
            {dayjsExt(currentBulletin?.createdAt).format('MMMM D, YYYY h:mm A')}
          </p>
        </div>
        <p>
          {currentItem + 1} of {props.bulletins.length}
        </p>
      </div>
    </div>
  );
}
